import React, { useEffect, useState } from 'react';
import { blogListingConfig } from '../app/config';

interface BlogFiltersProps {
    categoryFilterValues: string[];
    publishDateFilterValues: string[];
    appliedCategory: string;
    appliedPublishDate: string;
    searchText: string;
    currentCount: number;
    totalCount: number;
    isLoading: boolean;
    hasError: boolean;
    onSearchSubmit: (value: string) => void;
    onCategoryChange: (category: string) => void;
    onDateChange: (date: string) => void;
    onResetFilters: () => void;
}

export const BlogFilters: React.FC<BlogFiltersProps> = ({
    categoryFilterValues,
    publishDateFilterValues,
    appliedCategory,
    appliedPublishDate,
    searchText,
    currentCount,
    totalCount,
    isLoading,
    hasError,
    onSearchSubmit,
    onCategoryChange,
    onDateChange,
    onResetFilters,
}) => {
    const cfg = blogListingConfig;

    // Draft typing is local only — nothing happens until the form is submitted.
    // Resync when the applied search changes externally (reset, back/forward).
    const [searchDraft, setSearchDraft] = useState(searchText);
    useEffect(() => {
        setSearchDraft(searchText);
    }, [searchText]);

    const hasApplied = appliedCategory !== '' || appliedPublishDate !== '' || searchText !== '';

    return (
        <div className="stack blog-filters">
            <div className="blog-filters-top stack-lg">
                <div className="flex-between blog-filters-heading">
                    <h2 className="h3-style">{cfg.header}</h2>
                    {hasApplied && (
                        <button onClick={onResetFilters} className="blog-filters-reset">
                            {cfg.resetFiltersText}
                        </button>
                    )}
                </div>
                {/* Visually hidden for now (design TBD) — still announced to screen readers */}
                <p className="visually-hidden">
                    Showing {currentCount} of {totalCount} {totalCount === 1 ? 'result' : 'results'}
                </p>
            </div>

            <form
                className="blog-filters-search inline-input-form"
                role="search"
                onSubmit={(e) => {
                    e.preventDefault();
                    onSearchSubmit(searchDraft);
                }}
            >
                <label htmlFor="blogSearchInput" className="visually-hidden">{cfg.searchLabelText}</label>
                <input
                    type="search"
                    id="blogSearchInput"
                    placeholder={cfg.searchPlaceholderText}
                    value={searchDraft}
                    onChange={(e) => setSearchDraft(e.target.value)}
                />
                <button type="submit" className="btn-secondary blog-filters-search-submit" aria-label={cfg.searchButtonLabel}></button>
            </form>

            {isLoading && <div>Loading filters…</div>}
            {hasError && <div>Error loading filters.</div>}

            {/* .filter-options / .filter-option markup matches the Filter Block dropdown pattern */}
            <div className="filter-options">
                <div className="filter-option">
                    <label htmlFor="blogFilterDate">{cfg.dateFilterText}</label>
                    <select
                        id="blogFilterDate"
                        value={appliedPublishDate}
                        onChange={(e) => onDateChange(e.target.value)}
                    >
                        <option value="">{cfg.dateFilterDefaultChoice}</option>
                        {publishDateFilterValues.map((value) => (
                            <option key={value} value={value}>{value}</option>
                        ))}
                    </select>
                </div>

                <div className="filter-option">
                    <label htmlFor="blogFilterCategories">{cfg.categoryFilterText}</label>
                    <select
                        id="blogFilterCategories"
                        value={appliedCategory}
                        onChange={(e) => onCategoryChange(e.target.value)}
                    >
                        <option value="">{cfg.categoryFilterDefaultChoice}</option>
                        {categoryFilterValues.map((value) => (
                            <option key={value} value={value}>{value}</option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
};
